import { useEffect, useState } from "react";
import { Minus, Plus, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Mark } from "@/components/Logo";
import { useLang } from "@/context/LangContext";
import { useCart } from "@/context/CartContext";
import { useStore } from "@/context/StoreContext";
import { imgUrl, waLink } from "@/lib/api";

export function QtyStepper({ value, onChange, max, testid }) {
  return (
    <div className="inline-flex items-center border border-current/20" data-testid={testid}>
      <button data-testid={`${testid}-minus`} onClick={() => onChange(Math.max(1, value - 1))} className="p-3 opacity-70 transition-opacity hover:opacity-100" aria-label="-">
        <Minus className="h-3.5 w-3.5" strokeWidth={1.4} />
      </button>
      <span className="w-8 text-center font-latin text-sm" data-testid={`${testid}-value`}>{value}</span>
      <button data-testid={`${testid}-plus`} onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} className="p-3 opacity-70 transition-opacity hover:opacity-100 disabled:opacity-25" aria-label="+">
        <Plus className="h-3.5 w-3.5" strokeWidth={1.4} />
      </button>
    </div>
  );
}

function Notes({ p }) {
  const { t } = useLang();
  const rows = [["notes_top", p.notes_top], ["notes_heart", p.notes_heart], ["notes_base", p.notes_base]].filter((r) => r[1]);
  if (!rows.length) return null;
  return (
    <div className="mt-10">
      <p className="eyebrow">{t("pyramid")}</p>
      <div className="mt-4 divide-y divide-[#F7F3EB]/10 border-y border-[#F7F3EB]/10">
        {rows.map(([k, v]) => (
          <div key={k} className="grid grid-cols-[7.5rem_1fr] gap-4 py-4 text-sm">
            <span className="text-[#F7F3EB]/60">{t(k)}</span>
            <span className="text-[#F7F3EB]">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProductDrawer({ product, onClose }) {
  const { t, tr, fmt, rtl } = useLang();
  const { add, setOpen } = useCart();
  const { settings } = useStore();
  const [qty, setQty] = useState(1);
  const [img, setImg] = useState(0);
  const p = product;

  useEffect(() => {
    setQty(1);
    setImg(0);
  }, [p?.id]);

  const out = (p?.stock ?? 0) <= 0;
  const waOrder = settings?.whatsapp_enabled && settings?.whatsapp_number;
  const wa = settings?.whatsapp_number;

  const onAdd = () => {
    add(p, qty);
    toast.success(t("added"), { description: tr(p.name) });
    onClose();
    setTimeout(() => setOpen(true), 350);
  };

  return (
    <Sheet open={!!p} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side={rtl ? "left" : "right"} data-lenis-prevent data-testid="product-detail-drawer" className="w-full overflow-y-auto border-[#C6A76A]/20 bg-[#0B0B0C] p-0 text-[#F7F3EB] sm:max-w-xl">
        {p && (
          <div>
            <SheetTitle className="sr-only">{tr(p.name)}</SheetTitle>
            <div className="relative aspect-[4/4.4] overflow-hidden bg-[#141011]">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,rgba(198,167,106,0.22),transparent_65%)]" />
              {p.images?.[img] ? (
                <img src={imgUrl(p.images[img])} alt={tr(p.name)} className="absolute inset-0 h-full w-full object-cover" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Mark className="h-28 w-24 text-[#C6A76A]/40" />
                </div>
              )}
            </div>
            {p.images?.length > 1 && (
              <div className="flex gap-2 px-8 pt-4">
                {p.images.map((im, i) => (
                  <button key={im} data-testid={`product-thumb-${i}`} onClick={() => setImg(i)} className={`h-16 w-14 overflow-hidden border transition-colors ${i === img ? "border-[#C6A76A]" : "border-transparent opacity-60"}`}>
                    <img src={imgUrl(im)} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <div className="px-8 pb-12 pt-8">
              {tr(p.family) && <p className="eyebrow">{tr(p.family)}</p>}
              <h2 className="font-display mt-3 text-4xl sm:text-5xl" data-testid="product-detail-name">{tr(p.name)}</h2>
              <div className="mt-4 flex items-baseline gap-4">
                <span className="font-latin text-xl rtl:font-sans" data-testid="product-detail-price">{fmt(p.price)}</span>
                {p.compare_price > p.price && <span className="font-latin text-sm text-[#F7F3EB]/50 line-through">{fmt(p.compare_price)}</span>}
                {p.volume && <span className="font-latin text-xs tracking-[0.2em] text-[#F7F3EB]/60">{p.volume}</span>}
              </div>
              {tr(p.description) && <p className="mt-8 whitespace-pre-line text-base leading-loose text-[#F7F3EB]/80">{tr(p.description)}</p>}
              <Notes p={p} />
              <div className="mt-10 flex flex-wrap items-center gap-4">
                {!out && <QtyStepper value={qty} onChange={setQty} max={p.stock} testid="product-detail-qty" />}
                <button
                  data-testid="product-detail-add-to-cart"
                  disabled={out}
                  onClick={onAdd}
                  className="flex-1 bg-[#C6A76A] px-8 py-4 text-[0.78rem] tracking-[0.25em] text-[#0B0B0C] transition-colors duration-500 hover:bg-[#E3CA95] disabled:cursor-not-allowed disabled:bg-[#F7F3EB]/15 disabled:text-[#F7F3EB]/60 rtl:text-base rtl:tracking-normal"
                >
                  {out ? t("sold_out") : t("add_to_bag")}
                </button>
              </div>
              {!out && p.stock <= 3 && <p className="mt-3 text-xs text-[#E3CA95]">{t("only_left", { n: p.stock })}</p>}
              {wa && (
                <a
                  data-testid="product-detail-whatsapp-btn"
                  href={waLink(settings.whatsapp_number, waOrder ? `${t("wa_hello")}\n• ${tr(p.name)}${p.volume ? ` (${p.volume})` : ""} × ${qty} — ${fmt(p.price * qty)}` : `${t("wa_product_msg")} ${tr(p.name)}${p.volume ? ` (${p.volume})` : ""}`)}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 flex items-center justify-center gap-3 border border-[#F7F3EB]/25 py-4 text-[0.75rem] tracking-[0.22em] transition-colors hover:border-[#C6A76A] hover:text-[#C6A76A] rtl:text-sm rtl:tracking-normal"
                >
                  <MessageCircle className="h-4 w-4" strokeWidth={1.3} /> {waOrder ? t("order_whatsapp") : t("contact_wa")}
                </a>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
